/**
 * shrine.null.js — Variant selection replacement
 * Handles: variant-selects custom element
 * Updates: hidden input, price, button state, URL, media gallery
 */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    this.variantData = this.getVariantData();
    // Enable the hidden id input on load (it ships disabled)
    const input = this.getHiddenInput();
    if (input) input.removeAttribute('disabled');
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.updateHiddenInput();
    this.updateButton();
    this.updatePrice();
    this.updateMedia();
    this.updateURL();
    this.updateLabel(event.target);
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll('select, input[type="radio"]:checked')
    ).map((el) => el.value);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options
        .map((option, index) => this.options[index] === option)
        .includes(false);
    });
  }

  // ── Hidden input ────────────────────────────────────────────────────────────
  getHiddenInput() {
    const formId = this.closest('[data-section]')
      ? `product-form-${this.dataset.section}`
      : null;
    if (formId) {
      const form = document.getElementById(formId);
      if (form) return form.querySelector('input[name="id"]');
    }
    // Fallback: closest form
    const form = this.closest('form') || document.querySelector(`#product-form-${this.dataset.section}`);
    return form ? form.querySelector('input[name="id"]') : null;
  }

  updateHiddenInput() {
    const input = this.getHiddenInput();
    if (!input) return;
    if (this.currentVariant) {
      input.value = this.currentVariant.id;
      input.removeAttribute('disabled');
    } else {
      input.setAttribute('disabled', '');
    }
  }

  // ── Button ──────────────────────────────────────────────────────────────────
  updateButton() {
    const sectionId = this.dataset.section;
    const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!button) return;

    if (!this.currentVariant) {
      button.setAttribute('disabled', '');
      button.querySelector('.main-atc__label').innerHTML =
        window.variantStrings?.unavailable ||
        button.getAttribute('data-unavailable-text') ||
        'Unavailable';
      return;
    }

    if (this.currentVariant.available) {
      button.removeAttribute('disabled');
      button.removeAttribute('data-unavailable');
      const priceSpan = document.getElementById(`main-atc-price-${sectionId}`);
      if (priceSpan && priceSpan.dataset.showPrice === 'true') {
        priceSpan.textContent = this.formatMoney(this.currentVariant.price) + ' • ';
      }
      button.querySelector('.main-atc__label').innerHTML =
        (priceSpan ? priceSpan.outerHTML : '') +
        (window.variantStrings?.addToCart || 'Add to cart');
    } else {
      button.setAttribute('disabled', '');
      button.setAttribute('data-unavailable', 'true');
      button.querySelector('.main-atc__label').innerHTML =
        window.variantStrings?.soldOut || 'Sold out';
    }
  }

  // ── Price ───────────────────────────────────────────────────────────────────
  updatePrice() {
    if (!this.currentVariant) return;
    const sectionId = this.dataset.section;
    const priceContainer = document.getElementById(`price-${sectionId}`);
    if (!priceContainer) return;

    const price = this.currentVariant.price;
    const comparePrice = this.currentVariant.compare_at_price;

    // Regular price spans
    priceContainer.querySelectorAll('.price-item--regular').forEach((el) => {
      el.textContent = this.formatMoney(price);
    });

    // Sale price spans
    priceContainer.querySelectorAll('.price-item--sale').forEach((el) => {
      el.textContent = this.formatMoney(price);
    });

    // Compare price (crossed out)
    priceContainer.querySelectorAll('.price-item--regular').forEach((el) => {
      if (el.closest('.price__compare-price') || el.tagName === 'S') {
        el.textContent = this.formatMoney(comparePrice);
      }
    });

    // Toggle sale/regular state classes on the .price wrapper
    const priceEl = priceContainer.querySelector('.price');
    if (priceEl) {
      if (!this.currentVariant.available) {
        priceEl.classList.add('price--sold-out');
      } else {
        priceEl.classList.remove('price--sold-out');
      }

      if (comparePrice && comparePrice > price) {
        priceEl.classList.add('price--on-sale');
        // Update compare price text
        priceContainer.querySelectorAll('s.price-item--regular').forEach((el) => {
          el.textContent = this.formatMoney(comparePrice);
        });
      } else {
        priceEl.classList.remove('price--on-sale');
      }
    }

    // Sold out badge
    const soldOutBadge = priceContainer.querySelector('.price__badge-sold-out');
    if (soldOutBadge) {
      soldOutBadge.style.display = this.currentVariant.available ? 'none' : '';
    }

    // Sale badge
    const saleBadge = priceContainer.querySelector('.price__badge-sale');
    if (saleBadge) {
      saleBadge.style.display =
        comparePrice && comparePrice > price ? '' : 'none';
    }
  }

  // ── Media / Gallery ─────────────────────────────────────────────────────────
  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const sectionId = this.dataset.section;
    const mediaGallery = document.getElementById(`MediaGallery-${sectionId}`);
    if (!mediaGallery) return;

    const mediaId = `${sectionId}-${this.currentVariant.featured_media.id}`;

    // Use the existing MediaGallery custom element if available
    if (typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(mediaId, true);
      return;
    }

    // Fallback: find the media item and make it visible
    const mediaItem = mediaGallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaItem) {
      const allItems = mediaGallery.querySelectorAll('[data-media-id]');
      allItems.forEach((item) => item.classList.remove('is-active'));
      mediaItem.classList.add('is-active');
      mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  // ── URL ─────────────────────────────────────────────────────────────────────
  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  // ── Label on picker (shows selected value) ──────────────────────────────────
  updateLabel(changedInput) {
    const sectionId = this.dataset.section;
    // Update any label that shows the currently selected variant option
    const container = changedInput.closest('.product-form__input');
    if (!container) return;
    const label = container.querySelector('.form__label');
    if (!label) return;
    const customLabelSpan = container.querySelector(`[id^="custom-label-${sectionId}"]`);
    if (customLabelSpan) {
      customLabelSpan.querySelector
        ? (customLabelSpan.textContent = customLabelSpan.textContent.replace(
            /:\s*.+$/,
            `: ${changedInput.value}`
          ))
        : null;
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  getVariantData() {
    const el = this.querySelector('script[type="application/json"]');
    return el ? JSON.parse(el.textContent) : [];
  }

  formatMoney(cents) {
    if (!cents && cents !== 0) return '';

    // Detect format from an existing price in the DOM on first call
    if (this._currencySymbol === undefined) {
      const sample = document.querySelector('.price-item');
      const text = sample ? sample.textContent.trim() : '';
      // Extract currency symbol (everything that's not a digit, dot, comma or space)
      this._currencySymbol = text.replace(/[\d\s.,]/g, '').trim() || '$';
      // Detect if dots are used as thousands separator (e.g. $100.000)
      this._dotThousands = /\d\.\d{3}(\D|$)/.test(text);
      // Detect if no decimals are shown
      this._noDecimals = this._dotThousands || !/[.,]\d{2}(\D|$)/.test(text);
    }

    const amount = Math.round(cents / 100);

    if (this._dotThousands || this._noDecimals) {
      // Colombian / European format: $100.000
      // 'de-DE' locale uses dot for thousands, comma for decimals
      return this._currencySymbol + amount.toLocaleString('de-DE');
    }

    // Standard format with 2 decimals and comma thousands: $1,200.00
    return (
      this._currencySymbol +
      (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  }
}

customElements.define('variant-selects', VariantSelects);

// ── Secondary variant select (sticky ATC bar) ─────────────────────────────────
class SecondaryVariantSelect extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const select = this.querySelector('select.variant-dropdown');
    if (select) {
      select.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  onSelectChange(event) {
    const selectedVariantId = parseInt(event.target.value);
    const sectionId = this.dataset.section;

    // Sync the main variant-selects
    const mainPicker = document.querySelector(
      `variant-selects[data-section="${sectionId}"]`
    );
    if (!mainPicker) return;

    const variantData = mainPicker.getVariantData();
    const targetVariant = variantData.find((v) => v.id === selectedVariantId);
    if (!targetVariant) return;

    // Update each option input in the main picker
    targetVariant.options.forEach((optionValue, index) => {
      const position = index + 1;
      // Pills (radio)
      const radio = mainPicker.querySelector(
        `input[type="radio"][name]:checked ~ label, input[type="radio"][value="${CSS.escape(optionValue)}"]`
      );
      const radios = mainPicker.querySelectorAll(
        `input[type="radio"]`
      );
      radios.forEach((r) => {
        if (r.value === optionValue) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Dropdown (select)
      const selects = mainPicker.querySelectorAll('select');
      selects.forEach((sel) => {
        if (sel.querySelector(`option[value="${optionValue}"]`)) {
          sel.value = optionValue;
        }
      });
    });
  }
}

customElements.define('secondary-variant-select', SecondaryVariantSelect);
